// src/utils/dbUtils.js
// Archivo unificado que combina firebaseUtils.js y firestoreUtils.js
// db se importa internamente — ya no se pasa como parámetro a ninguna función.

import {db} from '../config/firebase';
import {
  collection, query, where, getDocs,
  doc, getDoc, addDoc, deleteDoc, setDoc,
  serverTimestamp,
} from 'firebase/firestore';

// ── Usuarios ──────────────────────────────────────────────────────────

export const getUserProfile = async (userId) => {
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() ? snap.data() : null;
};

export const getAllUserNames = async () => {
  const snap = await getDocs(collection(db, 'users'));
  const names = {};
  snap.forEach(d => {
    const data = d.data();
    names[d.id] = data.userName || data.name || d.id;
  });
  return names;
};

export const searchUsersByName = async (searchTerm) => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs
    .map(d => ({objectID: d.id, ...d.data()}))
    .filter(user =>
      (user.userName || user.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
};

export const formatTimestamp = (timestamp) =>
  new Date(timestamp?.seconds * 1000).toLocaleString();

export async function getFollowers(userId) {
  const q = query(collection(db, 'users'), where('following', 'array-contains', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.id);
}

export async function getFollowing(userId) {
  const snap = await getDoc(doc(db, 'users', userId));
  const data = snap.data();
  return data && Array.isArray(data.following) ? data.following : [];
}

// ── Feed / Following ──────────────────────────────────────────────────

export const fetchFollowing = async (userId) => {
  if (!userId) return [];
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    const data = snap.data();
    return data?.following ?? [];
  } catch (e) {
    console.error('[fetchFollowing]', e);
    return [];
  }
};

export const fetchUserNames = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.reduce((acc, d) => {
    acc[d.id] = d.data().name;
    return acc;
  }, {});
};

export const fetchRecipes = async (following, userNames) => {
  if (!Array.isArray(following) || following.length === 0) return [];
  const recipesList = [];

  for (const followUserId of following) {
    try {
      const userSnap = await getDoc(doc(db, 'users', followUserId));
      const userData = userSnap.data() || {};
      const rawShared = userData.recetasCompartidas;
      const sharedIds = Array.isArray(rawShared)
        ? rawShared.map(r => (typeof r === 'string' ? r : r?.id)).filter(Boolean)
        : [];

      if (sharedIds.length === 0) continue;

      const results = await Promise.allSettled(
        sharedIds.map(id => getDoc(doc(db, 'recetas', id)))
      );

      results.forEach((res, i) => {
        if (res.status !== 'fulfilled') {
          console.warn('[skip receta]', sharedIds[i], res.reason?.message || res.reason);
          return;
        }
        const snap = res.value;
        if (!snap.exists()) return;
        const data = snap.data() || {};
        recipesList.push({
          id: sharedIds[i],
          ...data,
          likes: Array.isArray(data.likes) ? data.likes : [],
          userName: userNames[followUserId] || userData.userName || userData.name || followUserId,
          userId: data.userId || followUserId,
        });
      });
    } catch (e) {
      console.error('[fetchRecipes] error para user', followUserId, e);
    }
  }

  recipesList.sort(
    (a, b) => (b.sharedAt?.toMillis?.() || 0) - (a.sharedAt?.toMillis?.() || 0)
  );
  return recipesList;
};

// ── Likes ─────────────────────────────────────────────────────────────

export const handleLike = async (recipeId, userId, setRecipes) => {
  const likeRef = doc(db, 'recetas', recipeId, 'likes', userId);
  const existing = await getDoc(likeRef);
  const recipeDoc = await getDoc(doc(db, 'recetas', recipeId));
  const recipeData = recipeDoc.data();
  const recipeOwnerId = recipeData?.userId;

  if (existing.exists()) {
    await deleteDoc(likeRef);
    setRecipes(prev => prev.map(r => r.id === recipeId
      ? {...r, likes: (r.likes || []).filter(id => id !== userId)}
      : r));
  } else {
    await setDoc(likeRef, {uid: userId, createdAt: Date.now()});
    setRecipes(prev => prev.map(r => r.id === recipeId
      ? {...r, likes: [...(r.likes || []), userId]}
      : r));
    if (recipeOwnerId && recipeOwnerId !== userId) {
      await addDoc(collection(db, 'notifications'), {
        type: 'like',
        recipientId: recipeOwnerId,
        senderId: userId,
        recipeId,
        read: false,
        timestamp: new Date(),
      });
    }
  }
};

// ── Comentarios ───────────────────────────────────────────────────────

export const handleAddComment = async (recipeId, userId, newComment, setNewComment, fetchComments) => {
  await addDoc(collection(db, 'comments'), {
    recipeId,
    userId,
    text: newComment,
    timestamp: new Date(),
  });
  setNewComment('');
  fetchComments(recipeId);

  const recipeDoc = await getDoc(doc(db, 'recetas', recipeId));
  const recipeOwnerId = recipeDoc.data()?.userId;
  if (recipeOwnerId && recipeOwnerId !== userId) {
    await addDoc(collection(db, 'notifications'), {
      recipientId: recipeOwnerId,
      senderId: userId,
      recipeId,
      type: 'comment',
      read: false,
      timestamp: serverTimestamp(),
    });
  }
};

export const fetchComments = async (recipeId, setComments) => {
  const q = query(collection(db, 'comments'), where('recipeId', '==', recipeId));
  const snap = await getDocs(q);
  const list = snap.docs.map(d => ({id: d.id, ...d.data()}));
  setComments(prev => ({...prev, [recipeId]: list}));
};
